import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

interface SEOConfig {
  title: string;
  description: string;
  canonical: string;
  ogTitle: string;
  ogDescription: string;
}

const BASE_URL = 'https://property-ready-shield.lovable.app';

const routeConfigs: Record<string, SEOConfig> = {
  '/': {
    title: 'PropertyShield AI | Unified Property Risk & Operations Platform',
    description: 'AI-powered property protection platform. Reduce losses by 30%, streamline maintenance operations, and optimize vendor management for commercial & multi-family properties.',
    canonical: `${BASE_URL}/`,
    ogTitle: 'PropertyShield AI | Unified Risk & Operations Platform',
    ogDescription: 'Reduce property losses by 30%. AI-powered protection for commercial & multi-family portfolios.',
  },
  '/vendor-tech-auth': {
    title: 'VendorLynx Pro | Field Service Management Platform',
    description: 'Mobile-first field service platform for vendor technicians. Real-time job dispatch, digital inspections, and time tracking.',
    canonical: `${BASE_URL}/vendor-tech-auth`,
    ogTitle: 'VendorLynx Pro | Field Service Management',
    ogDescription: 'Mobile-first platform for vendor technicians with real-time dispatch and digital inspections.',
  },
  '/maintenance-auth': {
    title: 'MaintTech Pro | Maintenance Team Management',
    description: 'Advanced maintenance management platform for property technicians. Work orders, inventory tracking, and predictive maintenance.',
    canonical: `${BASE_URL}/maintenance-auth`,
    ogTitle: 'MaintTech Pro | Maintenance Management',
    ogDescription: 'Advanced maintenance platform with predictive analytics and work order management.',
  },
  '/tenant-auth': {
    title: 'PropertyShield Resident Portal | Tenant Services',
    description: 'Submit maintenance requests, track work orders, book amenities, and communicate with property management.',
    canonical: `${BASE_URL}/tenant-auth`,
    ogTitle: 'PropertyShield Resident Portal',
    ogDescription: 'Your property management portal for maintenance requests, amenity bookings, and more.',
  },
  '/tenant': {
    title: 'PropertyShield Resident Portal | Home',
    description: 'View your dashboard, submit maintenance requests, book amenities, and stay connected with your property team.',
    canonical: `${BASE_URL}/tenant`,
    ogTitle: 'PropertyShield Resident Portal',
    ogDescription: 'Your resident dashboard for maintenance, amenities, and property communications.',
  },
  '/tenant-onboarding': {
    title: 'PropertyShield Resident Portal | Welcome Setup',
    description: 'Complete your resident profile setup to access maintenance requests, amenity bookings, and community features.',
    canonical: `${BASE_URL}/tenant-onboarding`,
    ogTitle: 'PropertyShield | Resident Onboarding',
    ogDescription: 'Set up your resident profile to get started with PropertyShield.',
  },
  '/tenant-directory': {
    title: 'PropertyShield | Tenant Directory',
    description: 'Browse and manage tenant profiles, lease status, and active maintenance requests across your properties.',
    canonical: `${BASE_URL}/tenant-directory`,
    ogTitle: 'PropertyShield | Tenant Directory',
    ogDescription: 'Manage tenant profiles and lease information across your property portfolio.',
  },
  '/tenant-engagement': {
    title: 'PropertyShield | Tenant Engagement',
    description: 'Monitor tenant satisfaction, communication metrics, and engagement analytics across your property portfolio.',
    canonical: `${BASE_URL}/tenant-engagement`,
    ogTitle: 'PropertyShield | Tenant Engagement',
    ogDescription: 'Track tenant satisfaction and engagement metrics for your properties.',
  },
  '/landing': {
    title: 'PropertyShield AI | Unified Property Risk & Operations Platform',
    description: 'AI-powered property protection platform. Reduce losses by 30%, streamline maintenance operations, and optimize vendor management for commercial & multi-family properties.',
    canonical: `${BASE_URL}/`,
    ogTitle: 'PropertyShield AI | Unified Risk & Operations Platform',
    ogDescription: 'Reduce property losses by 30%. AI-powered protection for commercial & multi-family portfolios.',
  },
};

function setMetaTag(selector: string, attribute: string, value: string) {
  const el = document.querySelector(selector);
  if (el) {
    el.setAttribute(attribute, value);
  }
}

const SEOHead: React.FC = () => {
  const location = useLocation();

  useEffect(() => {
    const config = routeConfigs[location.pathname] || routeConfigs['/'];

    document.title = config.title;
    setMetaTag('meta[name="description"]', 'content', config.description);
    setMetaTag('link[rel="canonical"]', 'href', config.canonical);
    setMetaTag('meta[property="og:title"]', 'content', config.ogTitle);
    setMetaTag('meta[property="og:description"]', 'content', config.ogDescription);
    setMetaTag('meta[property="og:url"]', 'content', config.canonical);
    setMetaTag('meta[name="twitter:title"]', 'content', config.ogTitle);
    setMetaTag('meta[name="twitter:description"]', 'content', config.ogDescription);
  }, [location.pathname]);

  return null;
};

export default SEOHead;
